import {
  SyntheticEvent, useEffect, useMemo, useRef, useState,
} from 'react';

import cloneDeep from 'lodash/cloneDeep';
import {
  Avatar, Autocomplete,
  AutocompleteRenderInputParams,
  AutocompleteChangeReason, AutocompleteChangeDetails,
  TextField,
} from '@mui/material';
import MailIcon from '@mui/icons-material/Mail';
import {
  get, isEmpty, debounce, isArray, castArray,
} from 'lodash';
import { SearchRounded } from '@mui/icons-material';
import { useApi } from 'lib/contexts/ApplicationState';
import { User, SearchableUser } from 'lib/types/users';
import { getAvatarOrPlaceholder } from 'lib/utils/imageUtils';

interface Props {
  value?: SearchableUser | SearchableUser[] | string;
  onChange: (event: SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<User | SearchableUser>,
  ) => void;
  label: string;
  size?: 'medium' | 'small';
  showSearchIcon?: boolean;
}

/** A simplified version of UserSearch that includes an exact email match
 * and is creatable through email but not twitter 
 * Could also become a new version of user search if/when Twitter kicks the bucket
 * */
export const SimpleUserSearch = ({
  value, onChange, label, size = 'medium', showSearchIcon,
}: Props) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<(string | User | SearchableUser)[]>([]);
  const isFetching = useRef(false);
  const API = useApi();
  const singleUserValue = !isArray(value) ? value : undefined;

  const fetchUsers = useMemo(
    () => debounce((request, callback) => {
      API.searchUsersForTeamMember(request.query)
        .then((userSearch) => {
          if (!userSearch) {
            throw new Error('failed to fetch users from gondola database');
          }
          return callback(userSearch);
        }).catch(() => {
          isFetching.current = false;
          setInputValue('');
        });
    }, 400),
    [],
  );

  useEffect(() => {
    isFetching.current = true;
    let active = true;
    const formattedValue = castArray(value);

    if (isEmpty(inputValue)) {
      setOptions(formattedValue || []);
      return undefined;
    }

    fetchUsers({ query: inputValue }, (results: SearchableUser[]) => {
      isFetching.current = false;
      if (active) {
        let newOptions: (string | User | SearchableUser)[] = [];
        if (value) {
          newOptions = formattedValue;
        }
        if (results) {
          newOptions = [...newOptions, ...results];
        }
        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchUsers]);

  return (
    <Autocomplete
      freeSolo
      getOptionLabel={(option: SearchableUser | User) => option?.name || ''}
      filterOptions={(theOptions: any, params: any) => {
        const filtered = cloneDeep(theOptions);
        // checks if any users are not in the database
        const showAddUser = filtered.length === 0 && params?.inputValue.match(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i);
        // Suggest the creation of a new value
        if (params.inputValue !== '' && !isFetching.current && showAddUser) {
          filtered.push({
            isCreatable: true,
            inputValue: params.inputValue,
            name: `Invite "${params.inputValue}"`,
          });
        }
        return filtered;
      }}
      //@ts-ignore
      options={options}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      autoComplete
      includeInputInList
      filterSelectedOptions
      //@ts-ignore
      value={value}
      onChange={onChange}
      onInputChange={(_: SyntheticEvent, newInputValue: string) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params: AutocompleteRenderInputParams) => {
        const inputProps = typeof singleUserValue !== 'string' && !isEmpty(value) ? {
          ...params.InputProps,
          startAdornment: <Avatar
            key={singleUserValue?.id}
            variant="circular"
            src={getAvatarOrPlaceholder(get(value, 'avatarUrl', ''), singleUserValue?.id)}
            sx={{ width: '24px', height: '24px', marginRight: '8px' }}
          />,
        } : { ...params.InputProps };

        return (
          <TextField
            {...params}
            autoFocus={showSearchIcon}
            InputProps={{
              ...inputProps,
              ...(showSearchIcon && {
                endAdornment: (
                  <div className="text-lightgray">
                    <SearchRounded />
                  </div>
                ),
              }),
            }}
            label={label || 'User'}
            fullWidth
            variant="outlined"
          />
        );
      }}
      renderOption={(renderProps, option) => (
        <li {...renderProps} key={`${option?.id || -1}-${option?.name || 'name'}`}>
          <div className="flex items-center">
            <div className="mr-4">
              {option?.id && (
                <Avatar
                  alt={option.name}
                  src={getAvatarOrPlaceholder(option.avatarUrl, option.id)}
                />
              )}
              {option && !option.id && <MailIcon />}
            </div>
            <div>
              <div className="font-semibold">
                {option?.name}
              </div>
              <div className="text-sm">
                {option?.username}
              </div>
            </div>
          </div>
        </li>
      )}
      size={size}
    />
  );
};
